import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import React, { useEffect, useRef, useState } from 'react';
import {
  useLazyGetKnowledgeBaseFileByIdQuery,
  useLazyGetKnowledgeBaseFileContentQuery,
  useUpdateKnowledgeBaseFileMutation,
} from 'src/services';
import { Skeleton } from 'src/components/ui/skeleton';
import { LoaderWrapper } from 'src/components/LoaderWrapper/LoaderWrapper';
import { Controller, useForm } from 'react-hook-form';
import Input from 'src/components/Input/Input';
import { Button } from 'src/components/ui/button';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { useLoader } from 'src/provider/LoaderProvider';
import { useNotification } from 'src/hooks/useNotification';
import { getFileExtension } from 'src/utils/fileUtils';
import { useKBContext } from 'src/provider/KnowledgeBaseProvider';
import { PlateEditor } from 'src/components/PlateEditor/PlateEditor';
import { useAppSelector } from 'src/store';
import { GOOGLE_URL } from 'src/constants/urls';

export const EditFilePopup = ({ item, knowledgeBase, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const itemExt = getFileExtension(item.name);
  const { setSkipRefetchFiles } = useKBContext();
  const { managedAccountId } = useAppSelector((s) => s.companySlices);
  const is: { file: boolean; txt: any; md: any; faq: any; readonly: boolean; content?: boolean } = {
    file: true,
    txt: item?.name?.endsWith('.txt'),
    md: item?.name?.endsWith('.md'),
    faq: item?.name?.endsWith('.faq'),
    readonly: false,
  };
  is.readonly = !(is.txt || is.md || is.faq);
  is.content = is.txt || is.md || is.faq;

  const loader = useLoader();
  const notification = useNotification();
  const [isLoading, setIsLoading] = useState(true);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [initialEditorValue, setInitialEditorValue] = useState('');
  const [fetchFile] = useLazyGetKnowledgeBaseFileByIdQuery();
  const [fetchContent] = useLazyGetKnowledgeBaseFileContentQuery();
  const [
    updateFile,
    { isLoading: isLoadingUpdateKnowledgeBaseFile, isSuccess: isSuccessUpdateKnowledgeBaseFile },
  ] = useUpdateKnowledgeBaseFileMutation();

  useEffect(() => {
    setSkipRefetchFiles(open);
  }, [open]);

  useEffect(() => {
    if (open) {
      if (is.file || is.faq) {
        setValue('name', is.faq ? item?.name.replace(/\.[^/.]+$/, '') : item.name);

        fetchFile({ id: knowledgeBase.id, file_id: item.id }).then((res: any) => {
          setDownloadUrl(res?.data?.download_url || null);

          if (!res?.data?.download_url) {
            setIsLoading(false);
          }
        });
      }
    }
  }, [open, item]);

  useEffect(() => {
    if (downloadUrl) {
      const isGoogleStorage = downloadUrl.startsWith(GOOGLE_URL.STORAGE);

      fetchContent({
        url: `${downloadUrl}${isGoogleStorage ? '' : managedAccountId ? `?account=${managedAccountId}` : ''}`,
      }).then((result: any) => {
        setFileContent(result.data);
        setIsLoading(false);
      });
    }
  }, [downloadUrl]);

  useEffect(() => {
    try {
      if (fileContent) {
        if (is.faq || is.md) {
          setInitialEditorValue(fileContent);
        } else {
          setValue('text', fileContent);
        }
      } else {
        if (is.faq || is.md) {
          setInitialEditorValue('');
        }
      }
    } catch (e) {
      setErrorMessage(true);
    }
  }, [fileContent]);

  useEffect(() => {
    if (isLoadingUpdateKnowledgeBaseFile) {
      setIsLoading(true);
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingUpdateKnowledgeBaseFile]);

  useEffect(() => {
    if (isSuccessUpdateKnowledgeBaseFile) {
      notification.success('Updated Successfully.');
      onOpenChange(false);
    }
  }, [isSuccessUpdateKnowledgeBaseFile]);

  const onOpenChange = (isOpen) => {
    setOpen(isOpen);

    if (!isOpen) {
      setTimeout(() => {
        setFileContent('');
        setIsLoading(true);
        setDownloadUrl(null);
        setErrorMessage(false);
        reset();
      }, 300);
    }
  };

  const { handleSubmit, trigger, control, setValue, reset, clearErrors } = useForm<any>({
    defaultValues: async () => ({
      name: item?.name || '',
      text: '',
    }),
    resolver: yupResolver(
      object({
        name: stringScheme({ required: true, minLength: 3, noslash: true })
          .label('File Name')
          .test('extension', `File Extension should be .${itemExt}`, (value) => {
            const ext = getFileExtension(value);

            return item.type === 'file' && itemExt ? itemExt === ext : true;
          }),
        text: stringScheme().label('File Text'),
      }),
    ),
  });

  const submit = ({ name, text }: { name: any; text?: any }) => {
    const body: Record<string, any> = { name: is.faq ? `${name}.faq` : name };

    if (!errorMessage && downloadUrl && text) {
      body.text = text;
    }

    updateFile({ knowledgeBaseId: knowledgeBase.id, fileId: item.id, body });
  };

  if (!is.content) {
    return null;
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogTrigger disabled={disabled} asChild>
          {is.readonly ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M1 11C1 11 4.375 5 10 5C15.625 5 19 11 19 11C19 11 15.625 17 10 17C4.375 17 1 11 1 11ZM10 14.8182C11.0443 14.8182 12.0458 14.4159 12.7842 13.6999C13.5227 12.9838 13.9375 12.0126 13.9375 11C13.9375 9.98736 13.5227 9.01619 12.7842 8.30014C12.0458 7.58409 11.0443 7.18182 10 7.18182C8.95571 7.18182 7.95419 7.58409 7.21577 8.30014C6.47734 9.01619 6.0625 9.98736 6.0625 11C6.0625 12.0126 6.47734 12.9838 7.21577 13.6999C7.95419 14.4159 8.95571 14.8182 10 14.8182Z"
                stroke="#05050D"
                strokeOpacity="0.5"
                strokeWidth="1.6"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
            >
              <path
                d="M1.66667 13.3306H2.845L10.6067 5.55634L9.42833 4.37609L1.66667 12.1504V13.3306ZM15 15H0V11.4584L11.1958 0.244385C11.3521 0.0879054 11.564 0 11.785 0C12.006 0 12.2179 0.0879054 12.3742 0.244385L14.7317 2.60572C14.8879 2.76224 14.9757 2.97451 14.9757 3.19584C14.9757 3.41717 14.8879 3.62944 14.7317 3.78597L5.2025 13.3306H15V15ZM10.6067 3.19584L11.785 4.37609L12.9633 3.19584L11.785 2.01559L10.6067 3.19584Z"
                fill="#05050D"
                fillOpacity="0.8"
              />
            </svg>
          )}
        </DialogTrigger>
        <DialogContent className={'relative overflow-hidden'}>
          <DialogHeader>
            <DialogTitle>{is.readonly ? 'View' : 'Edit'}</DialogTitle>
          </DialogHeader>
          <LoaderWrapper
            loader={
              <div className="flex md:gap-10 gap-4 w-full items-center">
                <div className="flex flex-col items-center w-full gap-5">
                  <Skeleton className="w-full min-h-[50px]" />
                  <Skeleton className="w-full min-h-[200px]" />
                </div>
              </div>
            }
            isLoading={isLoading}
          />
          {!isLoading && (
            <div className="grid gap-4 py-4">
              <form id={'edit-file-form'} onSubmit={handleSubmit(submit)}>
                <div className={'input-container flex flex-col gap-[3px]'}>
                  <Controller
                    control={control}
                    name="name"
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Input
                        autoFocus
                        type={'text'}
                        readonly={is.readonly}
                        label={is.faq ? 'Topic/Question' : 'File Name'}
                        placeholder={is.faq ? 'Enter a question or FAQ name here' : item.name}
                        invalidMessage={error?.message}
                        value={value}
                        isValid={!error?.message}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
              </form>
              {errorMessage ? (
                <p className={'text-center text-base text-ebony font-medium'}>
                  Cannot open file
                  <br /> Error: File includes a code snippet with unexpected characters "{`{,}`}",
                  etc.
                </p>
              ) : !is.content || !downloadUrl ? null : is.md || is.faq ? (
                <Controller
                  control={control}
                  name="text"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <PlateEditor
                      label={is.faq ? 'Content/Answer' : 'Text'}
                      initialValue={initialEditorValue}
                      onChange={onChange}
                      error={error}
                    />
                  )}
                />
              ) : (
                <Controller
                  control={control}
                  name="text"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <div>
                      <Input
                        type={'textarea'}
                        label={'Text:'}
                        readonly={is.readonly}
                        onChange={onChange}
                        value={value}
                        isValid={!error?.message}
                        invalidMessage={error?.message}
                        textareaRows={16}
                        textareaFieldSizing={'fixed'}
                        placeholder={'Some text here...'}
                        className={'max-h-[40vh] overflow-auto'}
                      />
                    </div>
                  )}
                />
              )}

              <div className="flex justify-center mt-5">
                <div className="flex gap-2.5">
                  <Button
                    variant={is.readonly ? 'default' : 'outline'}
                    type="button"
                    className="min-w-24"
                    onClick={() => onOpenChange(false)}
                  >
                    {is.readonly ? 'Close' : 'Cancel'}
                  </Button>
                  {!is.readonly && (
                    <Button form={'edit-file-form'} className="min-w-24">
                      <span>Update</span>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
